import { useEffect, useRef, useState } from 'react';

// ease-in-out: gentle at both the start and the end, so the number glides
// symmetrically whether it's counting up or dropping down (a plain ease-out
// makes drops fall off a cliff at the start).
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * useCountUp animates a displayed integer from its current value to `target`.
 *
 * Driven by requestAnimationFrame (not setInterval) so each update is synced to
 * the display refresh and cancelAnimationFrame stops the loop immediately on
 * target change or unmount. The CSS-Tricks article below covers the general
 * "tween a number" idea; this implementation is rAF-based rather than the
 * CSS/transition approach shown there:
 * https://css-tricks.com/animating-number-counters/
 *
 * On every target change it animates from wherever the display currently sits,
 * so consecutive updates (e.g. polled values) tween smoothly and interruptions
 * pick up from the current value. Honors prefers-reduced-motion.
 */
export default function useCountUp(target: number, durationMs = 800): number {
  const [displayedValue, setDisplayedValue] = useState(target);
  // Keep a ref in sync with the rendered value so each animation starts from
  // the currently displayed number without re-triggering the effect per frame.
  const displayedValueRef = useRef(displayedValue);
  displayedValueRef.current = displayedValue;

  useEffect(() => {
    const startValue = displayedValueRef.current;
    const endValue = target;

    if (startValue === endValue || prefersReducedMotion()) {
      setDisplayedValue(endValue);
      return;
    }

    let animationStartTime: number | null = null;
    let animationFrameId = 0;

    const animateFrame = (timestamp: number) => {
      if (animationStartTime === null) animationStartTime = timestamp;
      const progress = Math.min((timestamp - animationStartTime) / durationMs, 1);
      setDisplayedValue(Math.round(startValue + (endValue - startValue) * easeInOutCubic(progress)));
      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(animateFrame);
      }
    };
    animationFrameId = window.requestAnimationFrame(animateFrame);

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [target, durationMs]);

  return displayedValue;
}
